import React, { useCallback } from "react";
import { BoundaryData } from "../contexts/DataContext";
import { useData } from "../contexts/DataContext";
import MapRouteSidebar from "../components/MapRouteSidebar";
import SurfaceFeatures from "../components/SurfaceFeatures";
import { SiteMapSurfaceType } from "../DataAndMap";
import { useBlocker } from "react-router-dom";
import { useEditState } from "../contexts/EditStateContext";
import NotificationModal from "../../Modals/NotificationModal";
import useNavigateKeepSearchParams from "../../../hooks/useNavigateKeepSearchParams";

// this route only is shown / operates in editing mode
const SiteMapBoundaryRoute = ({
  boundary,
  initialSetup = false,
}: {
  boundary: BoundaryData | undefined;
  initialSetup?: boolean;
}) => {
  return (
    <>
      {!boundary ? (
        <NoBoundaryFound />
      ) : (
        <EditBoundaryDetails boundary={boundary} initialSetup={initialSetup} />
      )}
    </>
  );
};

export default SiteMapBoundaryRoute;

const BoundaryWrapper = ({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) => {
  return (
    <MapRouteSidebar noLink={true} title={title}>
      {children}
    </MapRouteSidebar>
  );
};

const NoBoundaryFound = () => {
  return (
    <BoundaryWrapper title="No Boundary">
      <div style={{ color: "red" }}>No boundary found</div>
    </BoundaryWrapper>
  );
};

const buildBoundaryFeature = (boundary: BoundaryData): SiteMapSurfaceType => {
  // Remove the last coordinate if it's the same as the first (to avoid duplicate closing points)
  const coords =
    boundary.coords.length > 1 &&
    boundary.coords[0][0] === boundary.coords[boundary.coords.length - 1][0] &&
    boundary.coords[0][1] === boundary.coords[boundary.coords.length - 1][1]
      ? boundary.coords.slice(0, -1)
      : boundary.coords;

  return {
    id: 1,
    type: "Polygon",
    coordinates: [
      coords.map((coord) => ({
        lat: coord[0],
        lng: coord[1],
      })),
    ],
    properties: {
      surfaceType: "boundary",
    },
  };
};

const convertPolygonCoordsToBoundary = (
  polygon: SiteMapSurfaceType,
): number[][] => {
  const coords = polygon.coordinates[0].map((coord) => [coord.lat, coord.lng]);
  // Add the first coordinate to the end to close the polygon
  if (coords.length > 0) {
    coords.push(coords[0]);
  }
  return coords;
};

const EditBoundaryDetails = ({
  boundary,
  initialSetup,
}: {
  boundary: BoundaryData;
  initialSetup: boolean;
}): JSX.Element => {
  const { setBoundary: setParentBoundary } = useData();
  const navigate = useNavigateKeepSearchParams(["return_to"]);
  const { isInvalid, setIsDirty } = useEditState();

  // useEffect(() => {
  //   if (initialSetup) {
  //     setIsInvalid(true);
  //     setInvalidInfo("Please create a boundary");
  //   }
  // }, [initialSetup, setIsInvalid, setInvalidInfo]);

  const blocker = useBlocker(useCallback(() => isInvalid, [isInvalid]));

  let features: SiteMapSurfaceType[] = [];

  if (boundary) {
    features = [buildBoundaryFeature(boundary)] as SiteMapSurfaceType[];
  }

  const leaveBoundaryEditMode = () => {
    navigate("..");
  };

  const setBoundary = (boundary?: BoundaryData) => {
    setIsDirty(true);
    if (boundary) {
      // setIsInvalid(false);
      // setInvalidInfo("");
    } else {
      // setIsInvalid(true);
      // setInvalidInfo("Please create a boundary");
    }
    setParentBoundary(boundary);
  };

  const deleteBoundary = () => {
    setBoundary(undefined);
  };

  const updateBoundary = (newPolygon: SiteMapSurfaceType) => {
    const newBoundary: BoundaryData = {
      id: newPolygon.id,
      coords: convertPolygonCoordsToBoundary(newPolygon),
      color: boundary.color, // Keep the existing color
    };
    setBoundary(newBoundary);
  };

  return (
    <>
      <BoundaryWrapper title={initialSetup ? "Add Boundary" : "Edit Boundary"}>
        <div className="text-sm">
          {initialSetup ? (
            <>
              <p>
                Please adjust this boundary to map ths property boundaries. This
                helps automate things like geofencing in this system.
              </p>
            </>
          ) : (
            <>
              {!boundary && (
                <>
                  <div style={{ color: "red" }}>
                    Please setup a property boundary so
                  </div>
                </>
              )}
              Property boundaries are needed to enable auto-clockin at
              properties using location data and geofencing.
            </>
          )}
        </div>
      </BoundaryWrapper>
      <SurfaceFeatures
        editing={true}
        selectedSurfaceId="1"
        features={features}
        surfaceTypes={[]}
        // May need to pass the full state object in here? working now ...
        featureVisibility={{}}
        onFeatureClick={leaveBoundaryEditMode}
        deleteSurfaceFeature={deleteBoundary}
        updateSurfaceFeature={(newPolygon: SiteMapSurfaceType) => {
          updateBoundary(newPolygon);
        }}
      />

      <NotificationModal
        button={{
          color: "blue",
          label: "Okay",
          onClick: () => {
            if (blocker && blocker.reset) {
              blocker.reset();
            }
          },
        }}
        open={blocker.state === "blocked"}
        subtitle="For geofencing to work smoothly, please setup a property boundary."
        title="Whoops!"
      />
    </>
  );
};
